import React, { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { ClipboardList, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (iso) => {
  if (!iso) return '';
  // Accept either YYYY-MM-DD or full datetime
  const d = iso.length === 10 ? new Date(iso + 'T00:00:00') : new Date(iso);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
};

const WorkLogWidget = () => {
  const [summary, setSummary] = useState('');
  const [hours, setHours] = useState('');
  const [recent, setRecent] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchRecent = useCallback(async () => {
    try {
      const r = await api.get('/work-logs/me/recent?days=7');
      setRecent(r.data || []);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  const submit = async () => {
    const s = summary.trim();
    if (!s) {
      toast.error('Write what you did today');
      return;
    }
    setSaving(true);
    try {
      await api.post('/work-logs', {
        summary: s,
        hours: hours ? Number(hours) : null,
      });
      setSummary('');
      setHours('');
      toast.success('Work log saved');
      fetchRecent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/work-logs/${id}`);
      setRecent(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Card className="bg-[#121212] border-white/10" data-testid="work-log-widget">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-[#F5A623]" />
          <h3 className="text-white font-medium text-sm">What I did today</h3>
        </div>
        <Textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="Edited 3 reels for Sharma wedding, shot indoor portraits, sent draft to client…"
          className="bg-[#0A0A0A] border-white/10 text-white text-sm min-h-[64px]"
          data-testid="work-log-summary"
        />
        <div className="flex items-center gap-2">
          <Input
            type="number"
            step="0.5"
            min="0"
            max="24"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="Hours (optional)"
            className="bg-[#0A0A0A] border-white/10 text-white text-sm w-40"
            data-testid="work-log-hours"
          />
          <Button
            size="sm"
            onClick={submit}
            disabled={saving}
            className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black"
            data-testid="work-log-save"
          >
            <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>

        {recent.length > 0 && (
          <div className="pt-2 border-t border-white/5 space-y-1.5">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Last 7 days</p>
            {recent.map((r) => (
              <div key={r.id} className="flex items-start gap-2 text-xs bg-white/[0.02] rounded px-2 py-1.5" data-testid={`work-log-row-${r.id}`}>
                <span className="text-zinc-500 w-14 shrink-0">{fmtDate(r.log_date)}</span>
                <span className="flex-1 text-white/80 whitespace-pre-wrap break-words">{r.summary}</span>
                {r.hours != null && (
                  <span className="text-[#F5A623] shrink-0">{r.hours}h</span>
                )}
                <button onClick={() => del(r.id)} className="text-zinc-500 hover:text-rose-400 shrink-0" data-testid={`work-log-delete-${r.id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkLogWidget;
